import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Landmark, Plus, Upload, X, Pencil, Trash2, ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mode = (en) => (en == null ? "off" : en ? "on" : "off");
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

function GroupCard({ g, onChanged }) {
  const [v, setV] = useState({
    rebate_mode: mode(g.rebate_enabled), rebate_percent: g.rebate_percent ?? "",
    charity_mode: mode(g.charity_enabled), charity_percent: g.charity_percent ?? "",
    charity_name: g.charity_name ?? "", charity_show_idle: !!g.charity_show_idle, charity_show_receipt: !!g.charity_show_receipt,
  });
  const [logo, setLogo] = useState(g.logo_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [newName, setNewName] = useState(g.name);
  const [delOpen, setDelOpen] = useState(false);
  const fileRef = useRef(null);

  const payload = (logoArg = logo, extra = {}) => {
    const rb = v.rebate_mode === "on" ? { enabled: true, percent: Number(v.rebate_percent) || 0 } : { enabled: false, percent: null };
    const ch = v.charity_mode === "on" ? { enabled: true, percent: Number(v.charity_percent) || 0 } : { enabled: false, percent: null };
    return {
      rebate_enabled: rb.enabled, rebate_percent: rb.percent,
      charity_enabled: ch.enabled, charity_percent: ch.percent,
      charity_name: v.charity_mode === "on" ? (v.charity_name || "") : "",
      charity_show_idle: v.charity_mode === "on" ? !!v.charity_show_idle : false,
      charity_show_receipt: v.charity_mode === "on" ? !!v.charity_show_receipt : false,
      logo_url: logoArg || "",
      ...extra,
    };
  };

  const save = async (logoArg, extra) => {
    setSaving(true);
    try {
      await api.put(`/building-groups/${encodeURIComponent(g.name)}`, payload(logoArg, extra));
      toast.success(`Saved ${g.name}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(r.data.url);
      await save(r.data.url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const removeLogo = async () => { setLogo(""); await save(""); };

  const doRename = async () => {
    const nn = (newName || "").trim();
    if (!nn || nn === g.name) { setRenameOpen(false); return; }
    try {
      await api.put(`/building-groups/${encodeURIComponent(g.name)}`, payload(logo, { new_name: nn }));
      toast.success(`Renamed to ${nn}`);
      setRenameOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/building-groups/${encodeURIComponent(g.name)}`);
      toast.success(`Deleted ${g.name}`);
      setDelOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sel = "mt-1 w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 text-sm";
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-3" data-testid={`group-card-${g.name}`}>
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-center overflow-hidden">
          {logo ? <img src={resolveUrl(logo)} alt={g.name} className="h-full w-full object-contain" data-testid={`group-logo-img-${g.name}`} />
                : <Landmark className="h-5 w-5 text-indigo-400" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-slate-900 dark:text-white truncate">{g.name}</h3>
            <button data-testid={`group-rename-${g.name}`} title="Rename group" onClick={() => { setNewName(g.name); setRenameOpen(true); }} className="text-slate-400 hover:text-blue-600 p-0.5"><Pencil className="h-3.5 w-3.5" /></button>
          </div>
          <p className="text-xs text-slate-400">{g.building_count || 0} building{(g.building_count || 0) === 1 ? "" : "s"}</p>
        </div>
        <button data-testid={`group-delete-${g.name}`} title="Delete group" onClick={() => setDelOpen(true)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="h-4 w-4" /></button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid={`group-logo-input-${g.name}`} />
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`group-logo-upload-${g.name}`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1.5" /> {logo ? "Replace logo" : "Upload logo"}</>}
        </Button>
        {logo && <Button variant="ghost" size="sm" onClick={removeLogo} data-testid={`group-logo-remove-${g.name}`} className="text-rose-600"><X className="h-3.5 w-3.5 mr-1" /> Remove</Button>}
      </div>

      <div className="grid grid-cols-2 gap-2 items-end pt-1 border-t border-slate-100 dark:border-slate-800">
        <div><Label className="text-xs">Rebate (% of net)</Label>
          <select data-testid={`group-rebate-mode-${g.name}`} value={v.rebate_mode} onChange={(e) => setV({ ...v, rebate_mode: e.target.value })} className={sel}><option value="off">Off</option><option value="on">On</option></select></div>
        {v.rebate_mode === "on" && <div><Label className="text-xs">Rebate %</Label><Input data-testid={`group-rebate-percent-${g.name}`} type="number" step="0.1" value={v.rebate_percent} onChange={(e) => setV({ ...v, rebate_percent: e.target.value })} className="mt-1 h-9" /></div>}
      </div>
      <div className="grid grid-cols-2 gap-2 items-end">
        <div><Label className="text-xs">Charity (% of net)</Label>
          <select data-testid={`group-charity-mode-${g.name}`} value={v.charity_mode} onChange={(e) => setV({ ...v, charity_mode: e.target.value })} className={sel}><option value="off">Off</option><option value="on">On</option></select></div>
        {v.charity_mode === "on" && <div><Label className="text-xs">Charity %</Label><Input data-testid={`group-charity-percent-${g.name}`} type="number" step="0.1" value={v.charity_percent} onChange={(e) => setV({ ...v, charity_percent: e.target.value })} className="mt-1 h-9" /></div>}
      </div>
      {v.charity_mode === "on" && (
        <>
          <div><Label className="text-xs">Charity name</Label><Input data-testid={`group-charity-name-${g.name}`} value={v.charity_name} onChange={(e) => setV({ ...v, charity_name: e.target.value })} placeholder="e.g. Shelter" className="mt-1 h-9" /></div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={v.charity_show_idle} onChange={(e) => setV({ ...v, charity_show_idle: e.target.checked })} /> Show on idle screen</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={v.charity_show_receipt} onChange={(e) => setV({ ...v, charity_show_receipt: e.target.checked })} /> Show on receipt</label>
          </div>
        </>
      )}
      <div className="flex justify-end"><Button data-testid={`group-save-${g.name}`} size="sm" onClick={() => save()} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></div>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename group</DialogTitle><DialogDescription>Renaming updates every building assigned to this group.</DialogDescription></DialogHeader>
          <Input data-testid="group-rename-input" value={newName} onChange={(e) => setNewName(e.target.value)} className="mt-1" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button data-testid="group-rename-save" onClick={doRename} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {g.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {(g.building_count || 0) > 0
                ? `${g.building_count} building(s) are in this group. They will be moved to "Ungrouped" (their own rebate/charity settings are kept). This can't be undone.`
                : "This permanently deletes the group. This can't be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="group-delete-confirm" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function BuildingGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/building-groups").then((r) => setGroups(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const n = (name || "").trim();
    if (!n) return;
    setCreating(true);
    try {
      await api.post("/building-groups", { name: n });
      toast.success(`Created ${n}`);
      setCreateOpen(false); setName("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Building Groups</h1>
          <p className="text-slate-500 mt-1">Groups are the top level of your estate (Group → Building → Location → Kiosk). Upload a logo and set group-level rebate &amp; charity — buildings inherit these unless overridden.</p>
        </div>
        <Button data-testid="group-new-button" onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700 shrink-0"><Plus className="h-4 w-4 mr-1.5" /> New group</Button>
      </div>
      {loading && <p className="text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></p>}
      {!loading && !groups.length && <p className="text-slate-400 text-sm">No building groups yet. Create one, then assign buildings to it in Sites &amp; Kiosks.</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {groups.map((g) => <GroupCard key={g.name} g={g} onChanged={load} />)}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New building group</DialogTitle><DialogDescription>Create a top-level group, then add a logo and rebate/charity settings.</DialogDescription></DialogHeader>
          <div><Label>Group name</Label><Input data-testid="group-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. FORA, GPE, Bruntwood" className="mt-1" /><p className="text-xs text-slate-400 mt-1">You can add a logo and rebate/charity settings after creating it.</p></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="group-new-save" onClick={create} disabled={creating} className="bg-blue-600 hover:bg-blue-700">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
